import React from "react";
import { History } from "lucide-react";
import type { RosterPatient } from "../providerApi";
import { StatusBadge } from "./FieldPrimitives";
import type { BadgeTone } from "./FieldPrimitives";
import "./replacement-status.css";

/**
 * A resident's hearing-aid replacement as the day sheet says it (Renata Silva, 2026-10-23): requested? still
 * pending? at which stage? an earlier one on record? The server decides every word
 * (backend services/manifest/replacementStatus.js); this file only shows it. Read-only.
 *
 * The payoff word comes from the latest paid-in-full check the PA team recorded, the same fact placing is refused
 * on. "Earlier replacement on record" is a cautious signal: whether a resident may have another replacement is not
 * ruled yet, so it is the earlier record, not a decision, and it blocks nothing.
 */
export type ReplacementState =
  | "NOT_REQUESTED"
  | "REQUESTED"
  | "IN_REVIEW"
  | "WAITING_PAYOFF"
  | "READY_TO_PLACE"
  | "PLACED"
  | "CANCELLED"
  | "USED_BEFORE";

export interface ReplacementStatus {
  state: ReplacementState;
  /** The server's short sentence, e.g. "Waiting on payoff confirmation". */
  label: string;
  /** What was asked for, since when, what it waits on, and the plain next step. */
  detail: string;
  requested_at: string | null;
  placed_at: string | null;
  /** The latest earlier replacement on record (a placed request or a replacement case), or null. */
  used_on: string | null;
  /** Whether the filing door would record a new request now (no open one; nothing placed in the last 30 days). */
  requestable?: boolean;
}

/**
 * The row's replacement, when the day sheet told it. Absent on a payload from before the deploy, on a row the
 * filing gate does not release, or when the read failed: then the screen says nothing about it.
 */
export function replacementOf(p: RosterPatient): ReplacementStatus | undefined {
  const r = (p as RosterPatient & { replacement?: ReplacementStatus | null }).replacement;
  return r && typeof r.state === "string" ? r : undefined;
}

/** May a request be offered for this row? When the day sheet said nothing, the old behaviour stands (the server still refuses a duplicate). */
export function replacementRequestable(status: ReplacementStatus | undefined): boolean {
  if (!status) return true;
  if (typeof status.requestable === "boolean") return status.requestable;
  return status.state === "NOT_REQUESTED" || status.state === "CANCELLED" || status.state === "USED_BEFORE";
}

const PENDING: ReplacementState[] = ["REQUESTED", "IN_REVIEW", "WAITING_PAYOFF", "READY_TO_PLACE"];

const CHIP: Record<Exclude<ReplacementState, "NOT_REQUESTED">, { words: string; tone: BadgeTone }> = {
  REQUESTED: { words: "Replacement requested", tone: "amber" },
  IN_REVIEW: { words: "Replacement: warranty check", tone: "amber" },
  WAITING_PAYOFF: { words: "Replacement: waiting on payoff", tone: "amber" },
  READY_TO_PLACE: { words: "Replacement: ready to place", tone: "indigo" },
  PLACED: { words: "Replacement placed", tone: "green" },
  CANCELLED: { words: "Replacement cancelled", tone: "neutral" },
  USED_BEFORE: { words: "Earlier replacement on record", tone: "indigo" },
};

const UNRULED_NOTE =
  "Whether a resident may have another replacement is not ruled yet. This is the earlier record, not a decision.";

/** "2026-10-23" -> "Oct 23, 2026" (a calendar day; never shifted by a time zone). */
export function dayWords(iso: string | null | undefined): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", { timeZone: "UTC", month: "short", day: "numeric", year: "numeric" }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}

/** The roster chip. Nothing for "No replacement requested": the workbench says that, the roster stays quiet. */
export function ReplacementChip({ status }: { status: ReplacementStatus | undefined }) {
  if (!status || status.state === "NOT_REQUESTED") return null;
  const chip = CHIP[status.state];
  if (!chip) return null;
  const tip = `${status.label}. ${status.detail}`;
  return (
    <span className="fw-repl-chip" title={tip} data-testid="replacement-chip">
      <StatusBadge tone={chip.tone}>
        {status.state === "USED_BEFORE" && <History aria-hidden="true" />}
        <span aria-hidden="true">{chip.words}</span>
        <span className="fw-sr-only">{`Replacement: ${status.label}`}</span>
      </StatusBadge>
    </span>
  );
}

/** Why the request button is not offered, in plain words. */
function notOfferedWords(status: ReplacementStatus): string {
  if (PENDING.includes(status.state))
    return "A replacement is already requested and the PA team is working on it. Nothing more is needed from you; if something has changed, tell the office.";
  if (status.state === "PLACED")
    return "This replacement was placed recently, so a new request is refused for now. If this is a new loss, tell the office.";
  return "A new request was placed for this resident in the last 30 days, so another is refused for now. If this is a new loss, tell the office.";
}

/**
 * The workbench's Replacement section: the persisted state (what is requested, since when, what it waits on, the
 * next step). `children` is the request form, shown only when the server would record a request.
 */
export function ReplacementSection({
  status,
  patientId,
  offerRequest,
  children,
}: {
  status: ReplacementStatus;
  /** The resident's id, for the heading's DOM id. Never the name: a name does not belong in markup ids. */
  patientId: string;
  /** The caller may ask here (the day sheet's gate), whatever the state. */
  offerRequest: boolean;
  children?: React.ReactNode;
}) {
  const id = `fw-repl-${String(patientId).replace(/[^A-Za-z0-9_-]+/g, "-")}`;
  const requestable = replacementRequestable(status);
  const chip = status.state === "NOT_REQUESTED" ? null : CHIP[status.state];
  const requested = dayWords(status.requested_at);
  const placed = dayWords(status.placed_at);
  const earlier = dayWords(status.used_on);
  return (
    <section className="fw-repl" aria-labelledby={`${id}-title`} data-testid="replacement-section">
      <h3 className="fw-replacement-title" id={`${id}-title`}>
        Replacement
      </h3>
      <p className="fw-repl-state" role="status">
        {chip ? <StatusBadge tone={chip.tone}>{status.label}</StatusBadge> : <strong>{status.label}</strong>}
      </p>
      <p className="fw-repl-detail">{status.detail}</p>
      {(requested || placed || status.used_on !== null) && (
        <dl className="fw-repl-facts">
          {requested && (
            <>
              <dt>Requested</dt>
              <dd>{requested}</dd>
            </>
          )}
          {placed && (
            <>
              <dt>Placed with the PA team</dt>
              <dd>{placed}</dd>
            </>
          )}
          {(status.used_on !== null || status.state === "USED_BEFORE") && (
            <>
              <dt>Earlier replacement on record</dt>
              <dd>
                {earlier || "Date not recorded"}
                <span className="fw-muted fw-repl-caution"> · {UNRULED_NOTE}</span>
              </dd>
            </>
          )}
        </dl>
      )}
      {status.state === "USED_BEFORE" && status.used_on === null && (
        <p className="fw-muted">{UNRULED_NOTE}</p>
      )}
      {offerRequest && (requestable ? children : <p className="fw-muted">{notOfferedWords(status)}</p>)}
    </section>
  );
}

/** The roster's Replacement filter: a client-side filter of the loaded page only. */
export type ReplacementFilter = "all" | "any" | "pending" | "payoff" | "placed" | "earlier";

export const REPLACEMENT_FILTERS: { value: ReplacementFilter; label: string }[] = [
  { value: "all", label: "All residents" },
  { value: "any", label: "Any replacement on record" },
  { value: "pending", label: "Requested, not yet placed" },
  { value: "payoff", label: "Waiting on payoff" },
  { value: "placed", label: "Placed" },
  { value: "earlier", label: "Earlier replacement on record" },
];

export function matchesReplacementFilter(status: ReplacementStatus | undefined, filter: ReplacementFilter): boolean {
  if (filter === "all") return true;
  if (!status) return false;
  switch (filter) {
    case "any":
      return status.state !== "NOT_REQUESTED";
    case "pending":
      return PENDING.includes(status.state);
    case "payoff":
      return status.state === "WAITING_PAYOFF";
    case "placed":
      return status.state === "PLACED";
    case "earlier":
      return status.state === "USED_BEFORE" || status.used_on !== null;
    default:
      return true;
  }
}
